import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  ApiResponse, 
  Recommendation, 
  Stock, 
  Brokerage, 
  AnalystTrend, 
  PriceHistory,
  SearchFilters,
  StockScore
} from '../types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: 'https://stock.tanmaynandwana.in/public/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        // Add auth token if needed
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error) => {
        console.error('API Error:', error);
        return Promise.reject(error);
      }
    );
  }

  // Build query params: flatten nested filters and normalize names expected by backend
  private buildQueryParams(filters?: SearchFilters): Record<string, any> {
    if (!filters) return {};

    const params: Record<string, any> = {};

    // Pagination
    if (filters.page) params.page = filters.page;
    if (filters.limit) params.limit = filters.limit;

    // Search text -> symbol (backend matches symbol/name)
    if (filters.query) params.symbol = filters.query;

    const f = filters.filters ?? {};

    if (f.recommendation) params.recommendation = f.recommendation;
    if (f.brokerage) params.brokerage = f.brokerage;
    if (typeof f.minUpside === 'number') params.minUpside = f.minUpside;
    if (typeof f.maxUpside === 'number') params.maxUpside = f.maxUpside;

    // Date range normalization
    if ((f as any).dateFrom) params.fromDate = (f as any).dateFrom;
    if ((f as any).dateTo) params.toDate = (f as any).dateTo;

    // Sort normalization
    if (f.sortBy) {
      // Map UI sort keys to API/JSON keys
      const sortMap: Record<string, string> = {
        potential_upside: 'upside',
        call_date: 'callDate',
        target_price: 'target_price',
        current_price: 'current_price'
      };
      params.sort = sortMap[f.sortBy] || f.sortBy;
    }
    if (f.sortOrder) params.order = f.sortOrder;

    return params;
  }

  // Recommendations
  async getRecommendations(filters?: SearchFilters): Promise<ApiResponse<Recommendation[]>> {
    const response = await this.api.get('/stocks', { params: this.buildQueryParams(filters) });
    return response.data;
  }

  async getOverviewStats(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/overview');
    return response.data;
  }

  async getRecommendationById(id: number): Promise<ApiResponse<Recommendation>> {
    const response = await this.api.get(`/stocks/${id}`);
    return response.data;
  }

  async getNewRecommendations(type?: 'all' | 'buy' | 'sell' | 'hold', filters?: SearchFilters): Promise<ApiResponse<Recommendation[]>> {
    const baseParams = this.buildQueryParams(filters);
    const specificParams = type && type !== 'all' ? { recommendation: type } : {};
    const response = await this.api.get('/stocks', { params: { ...baseParams, ...specificParams } });
    return response.data;
  }

  async getHighUpsideRecommendations(filters?: SearchFilters): Promise<ApiResponse<Recommendation[]>> {
    const params = filters?.limit ? { limit: filters.limit } : { limit: 12 };
    const response = await this.api.get('/stocks/high-upside', { params });
    return response.data;
  }

  async getHighDownsideRecommendations(filters?: SearchFilters): Promise<ApiResponse<Recommendation[]>> {
    const params = filters?.limit ? { limit: filters.limit } : { limit: 12 };
    const response = await this.api.get('/stocks/high-downside', { params });
    return response.data;
  }

  async getBrokerageRecommendations(brokerageSlug?: string, filters?: SearchFilters): Promise<ApiResponse<Recommendation[]>> {
    const baseParams = this.buildQueryParams(filters);
    const specificParams = brokerageSlug ? { brokerage: brokerageSlug } : {};
    const response = await this.api.get('/stocks', { params: { ...baseParams, ...specificParams } });
    return response.data;
  }

  async getAnalystTrends(stockId: number): Promise<ApiResponse<AnalystTrend[]>> {
    const response = await this.api.get(`/stocks/${stockId}/analyst-trends`);
    return response.data;
  }

  async getPriceHistory(stockId: number, days: number): Promise<ApiResponse<PriceHistory[]>> {
    const response = await this.api.get(`/stocks/${stockId}/price-history`, { params: { days } });
    return response.data;
  }

  // Stocks
  async getStocks(filters?: SearchFilters): Promise<ApiResponse<Stock[]>> {
    const response = await this.api.get('/stocks', { params: this.buildQueryParams(filters) });
    return response.data;
  }

  async getStockById(id: number): Promise<ApiResponse<Stock>> {
    const response = await this.api.get(`/stocks/${id}`);
    return response.data;
  }

  async getStockBySymbol(symbol: string): Promise<ApiResponse<Stock>> {
    const response = await this.api.get(`/stocks/symbol/${symbol}`);
    return response.data;
  }

  async searchStocks(query: string): Promise<ApiResponse<Stock[]>> {
    const response = await this.api.get('/stocks/search', { params: { q: query } });
    return response.data;
  }

  // Brokerages
  async getBrokerages(): Promise<ApiResponse<Brokerage[]>> {
    const response = await this.api.get('/stocks/brokerages/list');
    return response.data;
  }

  // Overview / dashboard metrics
  async getOverviewMetrics(): Promise<any> {
    const response = await this.api.get('/overview');
    return response.data;
  }

  // Get live stock details with Economic Times data
  async getLiveStockDetails(seoName: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/stocks/${seoName}/live-details`);
    return response.data;
  }

  // Stock Scores
  async getStockScores(filters?: {
    min_score?: number;
    min_upside?: number;
    min_recos?: number;
    sort?: string;
    order?: string;
    limit?: number;
  }): Promise<ApiResponse<StockScore[]>> {
    const response = await this.api.get('/stock-scores', { params: filters });
    return response.data;
  }

  async getStockScoreById(id: number): Promise<ApiResponse<StockScore>> {
    const response = await this.api.get(`/stock-scores/${id}`);
    return response.data;
  }

  // Generic POST method
  async post(endpoint: string, data?: any): Promise<any> {
    const response = await this.api.post(endpoint, data);
    return response.data;
  }

  // Generic GET method
  async get(endpoint: string, params?: any): Promise<any> {
    const response = await this.api.get(endpoint, { params });
    return response;
  }
}

export const apiService = new ApiService();
export default apiService;